class Upsell extends HTMLElement {
  constructor() {
    super();

    // Feature toggle: Auto-sync upsell variants when main product variant changes
    // Set to false to disable automatic variant matching
    this.ENABLE_VARIANT_SYNC = false;

    this.formId = this.dataset.formId;
    this.init();
    this.setupVariantMatching();
    this.createMissingHiddenInputs();
  }

  init() {
    this.querySelectorAll("input[name='upsell']").forEach((item, index) => {
      item.dataset.index = index;
      item.addEventListener("change", (event) => {
        this.handleChange(event.target);
      });

      // Initial quantity sync: Ensure unchecked items have quantity = 0
      this.updateQuantityForAllForms(index, item.checked);
    });
  }

  setupVariantMatching() {
    // Early return if variant sync is disabled
    if (!this.ENABLE_VARIANT_SYNC) {
      return;
    }

    const initialOptions = this.getCurrentMainProductOptions();
    if (initialOptions.length > 0) {
      this.updateUpsellVariantsLightweight(initialOptions);
    }

    document.addEventListener("variant:change", (event) => {
      if (event.detail?.variant) {
        this.handleVariantChangeDebounced(event.detail.variant);
      }
    });

    document.addEventListener("product:rerender", (event) => {
      setTimeout(() => {
        const currentOptions = this.getCurrentMainProductOptions();
        if (currentOptions.length > 0) {
          this.updateUpsellVariants(currentOptions);
        }
      }, 100);
    });
  }

  handleChange(target) {
    const checked = target.checked;
    const index = target.dataset.index;

    this.updateQuantityForAllForms(index, checked);
  }

  createMissingHiddenInputs() {
    const allStickyForms = document.querySelectorAll(
      'form[id*="product-form-sticky"]'
    );
    const upsellCheckboxes = this.querySelectorAll("input[name='upsell']");

    allStickyForms.forEach((stickyForm) => {
      upsellCheckboxes.forEach((checkbox, index) => {
        const idInputName = `items[${index}][id]`;
        if (!stickyForm.querySelector(`input[name="${idInputName}"]`)) {
          const idInput = document.createElement("input");
          idInput.type = "hidden";
          idInput.name = idInputName;
          idInput.value = checkbox.value;
          idInput.dataset.upsellItem = index;
          stickyForm.appendChild(idInput);
        }

        const quantityInputName = `items[${index}][quantity]`;
        if (!stickyForm.querySelector(`input[name="${quantityInputName}"]`)) {
          const quantityInput = document.createElement("input");
          quantityInput.type = "hidden";
          quantityInput.name = quantityInputName;
          quantityInput.value = "0";
          quantityInput.dataset.upsellQuantity = index;
          stickyForm.appendChild(quantityInput);
        }
      });
    });
  }

  updateQuantityForAllForms(index, checked) {
    const quantityValue = checked ? 1 : 0;
    const quantityInputName = `items[${index}][quantity]`;

    const mainQuantityInput = document.querySelector(
      `input[name="${quantityInputName}"][form="${this.formId}"]`
    );
    if (mainQuantityInput) {
      mainQuantityInput.value = quantityValue;
    }

    const allStickyForms = document.querySelectorAll(
      'form[id*="product-form-sticky"]'
    );
    allStickyForms.forEach((stickyForm) => {
      const stickyQuantityInput = stickyForm.querySelector(
        `input[name="${quantityInputName}"]`
      );
      if (stickyQuantityInput) {
        stickyQuantityInput.value = quantityValue;
      }
    });
  }

  getCurrentMainProductOptions() {
    const mainOptions = [];

    const productDetailVariation = document.querySelector(
      "product-detail-variation"
    );
    if (productDetailVariation) {
      const realVariantInputs = productDetailVariation.querySelectorAll(
        'input[type="radio"]:checked'
      );
      realVariantInputs.forEach((input) => {
        if (!input.name.includes("variant-group-")) {
          const normalized = this.normalizeOptionValue(input.value);
          mainOptions.push(normalized);
        }
      });

      const realSelects = productDetailVariation.querySelectorAll("select");
      realSelects.forEach((select) => {
        if (select.value && !select.closest("product-detail-variant-group")) {
          const normalized = this.normalizeOptionValue(select.value);
          mainOptions.push(normalized);
        }
      });
    }

    return mainOptions;
  }

  handleVariantChangeDebounced(variant) {
    clearTimeout(this.variantChangeTimeout);
    this.variantChangeTimeout = setTimeout(() => {
      this.handleVariantChange(variant);
    }, 100);
  }

  handleVariantChange(variant) {
    if (!variant) return;

    const selectedOptions =
      variant.selected_options || variant.selectedOptions || [];
    if (selectedOptions.length === 0) return;

    const mainOptions = selectedOptions.map((option) => {
      const value = option.value || option.name || option;
      return this.normalizeOptionValue(value);
    });

    this.updateUpsellVariants(mainOptions);
  }

  updateUpsellVariantsLightweight(mainOptions) {
    const upsellItems = this.querySelectorAll("[data-upsell-matcher]");

    upsellItems.forEach((upsellItem) => {
      const currentVariantId = upsellItem.dataset.currentVariantId;
      const matchingVariantId = this.findMatchingVariant(
        upsellItem,
        mainOptions
      );

      if (matchingVariantId && matchingVariantId !== currentVariantId) {
        this.updateUpsellItemCheckboxOnly(upsellItem, matchingVariantId);
      }
    });
  }

  updateUpsellItemCheckboxOnly(upsellItem, newVariantId) {
    const currentCheckbox = upsellItem.querySelector('input[type="checkbox"]');
    if (!currentCheckbox) return;

    currentCheckbox.value = newVariantId;
    upsellItem.dataset.currentVariantId = newVariantId;
  }

  updateUpsellVariants(mainOptions) {
    const upsellItems = this.querySelectorAll("[data-upsell-matcher]");

    upsellItems.forEach((upsellItem) => {
      const currentVariantId = upsellItem.dataset.currentVariantId;
      const matchingVariantId = this.findMatchingVariant(
        upsellItem,
        mainOptions
      );

      if (matchingVariantId && matchingVariantId !== currentVariantId) {
        this.updateUpsellItem(upsellItem, matchingVariantId);
      }
    });
  }

  findMatchingVariant(upsellItem, mainOptions) {
    const availableVariants = upsellItem.dataset.availableVariants;
    if (!availableVariants) return null;

    const variants = availableVariants.split(";").map((entry) => {
      const [optionsStr, variantId] = entry.split(":");
      const options = optionsStr
        ? optionsStr.split("|").filter((o) => o !== "")
        : [];
      return { options, id: variantId };
    });

    const matchingVariant = variants.find((variant) => {
      return variant.options.some((upsellOption) => {
        return mainOptions.some((mainOption) => {
          return upsellOption.includes(mainOption);
        });
      });
    });

    return matchingVariant ? matchingVariant.id : null;
  }

  updateUpsellItem(upsellItem, newVariantId) {
    const currentCheckbox = upsellItem.querySelector('input[type="checkbox"]');
    if (!currentCheckbox) return;

    currentCheckbox.value = newVariantId;
    upsellItem.dataset.currentVariantId = newVariantId;
  }

  normalizeOptionValue(value) {
    return value
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }
}

if (!window.customElements.get("kc-upsell")) {
  window.customElements.define("kc-upsell", Upsell);
}
